/**
 * Шаблоны осмотра.
 *
 * CLAUDE.md: шаблоны data-driven, в БД, а не в коде — смена набора узлов
 * это смена данных. И шаблон генерируется ПОД КОНФИГУРАЦИЮ конкретного юнита:
 * у тягача 6x4 десять колёс, у полуприцепа восемь, и пороги на рулевой оси
 * строже, чем на остальных.
 */
import { z } from 'zod';
import { localeSchema, objectIdSchema } from './common.js';
import { legalLimitFor, MEASUREMENT_SPECS, measurementKindSchema, } from './measurement.js';
import { brakePositions, wheelPositions, } from './unit.js';
export const ITEM_TYPES = ['boolean', 'measurement', 'photo'];
/** По чему пункт размножается на позиции. */
export const EXPAND_BY = ['none', 'wheels', 'brakes'];
export const localizedTextSchema = z.partialRecord(localeSchema, z.string().min(1).max(300));
export const templateItemSchema = z
    .object({
    key: z.string().min(1).max(64).regex(/^[a-z0-9_]+$/),
    labels: localizedTextSchema,
    type: z.enum(ITEM_TYPES),
    expandBy: z.enum(EXPAND_BY).default('none'),
    measurementKind: measurementKindSchema.nullable().default(null),
    /**
     * Обязательное фото — только там, где оно юридически или финансово окупается.
     * Продукт, который дольше бумаги, водитель убивает за две недели.
     */
    photoRequired: z.boolean().default(false),
    /** Отказ по этому пункту — критический дефект. */
    critical: z.boolean().default(false),
})
    .refine((i) => i.type !== 'measurement' || i.measurementKind !== null, {
    message: 'measurementKind is required for a measurement item',
    path: ['measurementKind'],
})
    .refine((i) => i.type === 'measurement' || i.measurementKind === null, {
    message: 'measurementKind is only allowed on a measurement item',
    path: ['measurementKind'],
});
export const templateSectionSchema = z.object({
    key: z.string().min(1).max(64).regex(/^[a-z0-9_]+$/),
    labels: localizedTextSchema,
    items: z.array(templateItemSchema).min(1),
});
export const createTemplateSchema = z.object({
    key: z.string().min(1).max(64).regex(/^[a-z0-9_]+$/),
    labels: localizedTextSchema,
    appliesTo: z.enum(['tractor', 'trailer']),
    sections: z.array(templateSectionSchema).min(1),
});
export const templateSchema = createTemplateSchema.extend({
    id: objectIdSchema,
    orgId: objectIdSchema,
    version: z.number().int().positive(),
    publishedAt: z.coerce.date(),
});
/* ------------------------------- пороги ---------------------------------- */
export const thresholdSchema = z.object({
    kind: measurementKindSchema,
    /** Роль оси (для колёс) или тип камеры (для тормозов). */
    appliesTo: z.string().min(1).max(32),
    warn: z.number(),
    critical: z.number(),
});
function thresholdFor(kind, appliesTo, overrides) {
    const legal = legalLimitFor(kind, appliesTo);
    const custom = overrides.find((t) => t.kind === kind && t.appliesTo === appliesTo);
    return {
        warn: custom?.warn ?? null,
        critical: custom?.critical ?? legal?.limit ?? null,
        legalLimit: legal?.limit ?? null,
        citation: legal?.citation ?? null,
    };
}
/**
 * Разворачивает шаблон под конкретный юнит.
 *
 * Чистая функция и живёт в contracts: результат вмораживается в запись осмотра
 * (правило 2), поэтому фронт и бэк обязаны получать побайтово одно и то же.
 */
export function resolveTemplate(params) {
    const { template, axleConfig, thresholds = [] } = params;
    const wheels = wheelPositions(axleConfig);
    const brakes = brakePositions(axleConfig);
    const sections = template.sections.map((section) => {
        const items = [];
        for (const item of section.items) {
            const spec = item.measurementKind === null ? null : MEASUREMENT_SPECS[item.measurementKind];
            const base = {
                itemKey: item.key,
                sectionKey: section.key,
                type: item.type,
                labels: item.labels,
                measurementKind: item.measurementKind,
                measurementUnit: spec?.unit ?? null,
                photoRequired: item.photoRequired,
                critical: item.critical,
            };
            if (item.expandBy === 'none') {
                items.push({
                    ...base,
                    id: item.key,
                    positionId: null,
                    positionLabel: null,
                    threshold: null,
                });
                continue;
            }
            const positions = item.expandBy === 'wheels'
                ? wheels.map((p) => ({ id: p.id, label: p.label, appliesTo: p.role }))
                : brakes.map((p) => ({
                    id: p.id,
                    label: p.label,
                    appliesTo: p.chamberType,
                }));
            for (const position of positions) {
                items.push({
                    ...base,
                    id: `${item.key}@${position.id}`,
                    positionId: position.id,
                    positionLabel: position.label,
                    threshold: item.measurementKind === null
                        ? null
                        : thresholdFor(item.measurementKind, position.appliesTo, thresholds),
                });
            }
        }
        return { key: section.key, labels: section.labels, items };
    });
    return {
        templateKey: template.key,
        templateVersion: template.version,
        appliesTo: template.appliesTo,
        sections,
        itemCount: sections.reduce((sum, s) => sum + s.items.length, 0),
    };
}
export function evaluateMeasurement(kind, value, threshold) {
    if (threshold === null)
        return 'ok';
    const worse = MEASUREMENT_SPECS[kind].worse;
    const isWorseOrEqual = (limit) => worse === 'lower' ? value <= limit : value >= limit;
    if (threshold.critical !== null && isWorseOrEqual(threshold.critical))
        return 'critical';
    if (threshold.warn !== null && isWorseOrEqual(threshold.warn))
        return 'warn';
    return 'ok';
}
//# sourceMappingURL=template.js.map